/*
 * File: apply-tags.ts
 * File Created: 2023-12-19 08:58:12
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-10-23 04:11:53
 * Modified By: Val Liu
 * -----
 */

import { ObjectHelper } from "@root/core";
import {
    EUITagTarget,
    IUITag,
    IUITagInput,
    IUITagMoreTagsUnit,
    IUITagVariables,
    UITag,
} from "./core";
import "../index";
import { IBaseComponentUITag } from "@root/components/interfaces";

/// apply all meta tags
export function applyTags(
    target: EUITagTarget,
    input: Omit<IUITagInput, "rule">,
    uiTag?: IBaseComponentUITag,
): Omit<IUITagInput, "rule"> {
    const { metas, metaIndex, meta } = input;
    if (meta == null) return input;
    const attrs = meta.attr.tags;
    const attrsMap = meta.attr.tagMap;

    /// find all default tags
    const defaultTags = UITag.get({ default: true });
    if (defaultTags != null) {
        /// loop through
        for (const tag of defaultTags) {
            const defName = tag.ctor.name;
            /// default tags only work on not defined meta
            if (attrsMap[defName] != null) continue;

            const tagInstance = tag.instance;
            /// check allowance
            if (!checkApplicable(target, tagInstance)) continue;

            /// apply tag
            input.inputs = ObjectHelper.deepMerge(
                input.inputs,
                tagInstance.parse({
                    ...input,
                    isDefault: true,
                }),
            );
        }
    }

    const doAttr = (attr: (typeof attrs)[number]) => {
        const [name, rule] = attr;
        const tag = UITag.get(name);
        if (tag == null) {
            console.error(`<applyTags> cannot find tag <${name}>.`);
            return;
        }

        const tagInstance = tag.instance;
        /// check allowance
        if (!checkApplicable(target, tagInstance)) return;
        /// apply tag
        input.inputs = ObjectHelper.deepMerge(
            input.inputs,
            tagInstance.parse({
                ...input,
                rule,
            }),
        );
    };

    let moreTags: IUITagMoreTagsUnit[] = [];
    const pushMoreTags = () => {
        let smoreTags = input.inputs.specials?.moreTags;
        if (!smoreTags?.length) return;
        moreTags.push(...smoreTags);
        smoreTags.length = 0;
    };
    const doMoreTags = (): boolean => {
        if (moreTags.length === 0) return false;
        const unit = moreTags.shift();
        if (unit == undefined) return true;
        doAttr([unit.tag, unit.rule]);
        return true;
    };
    const executeMoreTags = () => {
        pushMoreTags();
        /// execute moreTags
        while (doMoreTags() === true) {
            pushMoreTags();
        }
    };
    /// from default
    executeMoreTags();
    /// loop through the attrs
    for (let attr of attrs) {
        doAttr(attr);
        executeMoreTags();
    }

    /// finally, handle additional meta
    if (uiTag != null) {
        Object.keys(uiTag).forEach((tag) => {
            const rule = uiTag[tag];
            moreTags.push({ tag, rule });
            executeMoreTags();
        });
    }

    return input;
}

/// private helper
function checkApplicable(target, tagInstance) {
    const list: any[] = tagInstance.appliedTo;
    /// if null allownace, return true
    if (list == null) return true;
    return list.includes(target);
}
